import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ChevronDown, ChevronRight, Hash, MessageCircle, Plus, Archive, PenLine, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import CreateChannelModal from './CreateChannelModal';
import CreateDMModal from './CreateDMModal';
import { getDraft, useDraftVersion } from './ChatInput';
import { getDMDisplayName } from '../../utils/chatUtils';
import { sanitizeHtml } from '../../lib/sanitize';
import { useAuthStore } from '../../store/authStore';
import { getChannels, searchAllMessages, type ChatChannelItem } from '../../services/chatChannelService';
import { useOrgStore } from '../../store/orgStore';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '@maios/shared';
import { useMyFavorites } from '../../hooks/useMyHome';

export default function ChatSubSidebar() {
  const navigate = useNavigate();
  const { channelId: activeChannelId } = useParams();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDMModal, setShowDMModal] = useState(false);
  const [mentionedChannels, setMentionedChannels] = useState<Set<string>>(new Set());
  useDraftVersion(); // 초안 변경 시 리렌더 트리거

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 300);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['chatChannels', activeOrgId, debouncedSearch],
    queryFn: () => getChannels({ search: debouncedSearch || undefined }),
    enabled: !!activeOrgId,
    refetchInterval: 30_000, // 30초마다 폴링 (Socket 누락 대비)
  });

  const channels = data?.channels ?? [];

  // Favorites
  const { data: favoriteChannels } = useMyFavorites('CHANNEL');
  const favoriteIds = useMemo(() => new Set((favoriteChannels ?? []).map((f) => f.targetId)), [favoriteChannels]);

  // Archived channels
  const { data: archivedData } = useQuery({
    queryKey: ['chatChannels', activeOrgId, 'archived'],
    queryFn: () => getChannels({ archived: 'true' }),
    enabled: !!activeOrgId,
    staleTime: 60_000,
  });
  const archivedChannels = archivedData?.channels ?? [];

  // Message search (when query >= 2 chars)
  const { data: msgSearchData } = useQuery({
    queryKey: ['chatMessageSearch', debouncedSearch],
    queryFn: () => searchAllMessages({ q: debouncedSearch, limit: 5 }),
    enabled: !!activeOrgId && debouncedSearch.length >= 2,
  });
  const messageResults = msgSearchData?.results ?? [];

  const sections = useMemo(() => {
    const favorites = channels.filter((c) => favoriteIds.has(c.id));
    const project = channels.filter((c) => c.type === 'PROJECT' && !favoriteIds.has(c.id));
    const user = channels.filter((c) => c.type === 'USER' && !favoriteIds.has(c.id));
    const dm = channels.filter((c) => c.type === 'DM' && !favoriteIds.has(c.id));
    return { favorites, project, user, dm };
  }, [channels, favoriteIds]);

  // Socket: listen for unread updates (with reconnect handling)
  useEffect(() => {
    const handleUnread = () => {
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
    };

    let socket = getSocket();

    const handleMentioned = (data: { channelId: string; channelName: string; mentionedBy: { name: string } }) => {
      setMentionedChannels((prev) => new Set(prev).add(data.channelId));
    };
    const handleArchived = (data: { channelId: string; channelName: string }) => {
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      toast(`'${data.channelName}' 채널이 아카이브되었습니다.`, { icon: '📦' });
    };
    const handleUnarchived = (data: { channelId: string; channelName: string }) => {
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      toast(`'${data.channelName}' 채널의 아카이브가 해제되었습니다.`, { icon: '📦' });
    };

    const registerListeners = (s: NonNullable<ReturnType<typeof getSocket>>) => {
      s.on(SOCKET_EVENTS.CHANNEL_UNREAD_UPDATE, handleUnread);
      s.on(SOCKET_EVENTS.CHANNEL_NEW_MESSAGE, handleUnread);
      s.on(SOCKET_EVENTS.CHAT_UNREAD_UPDATE, handleUnread);
      s.on(SOCKET_EVENTS.CHANNEL_INVITED, handleUnread);
      s.on(SOCKET_EVENTS.CHANNEL_KICKED, handleUnread);
      s.on(SOCKET_EVENTS.CHANNEL_MEMBER_KICKED, handleUnread);
      s.on(SOCKET_EVENTS.CHANNEL_MENTIONED, handleMentioned);
      s.on(SOCKET_EVENTS.CHANNEL_ARCHIVED, handleArchived);
      s.on(SOCKET_EVENTS.CHANNEL_UNARCHIVED, handleUnarchived);
    };

    const unregisterListeners = (s: NonNullable<ReturnType<typeof getSocket>>) => {
      s.off(SOCKET_EVENTS.CHANNEL_UNREAD_UPDATE, handleUnread);
      s.off(SOCKET_EVENTS.CHANNEL_NEW_MESSAGE, handleUnread);
      s.off(SOCKET_EVENTS.CHAT_UNREAD_UPDATE, handleUnread);
      s.off(SOCKET_EVENTS.CHANNEL_INVITED, handleUnread);
      s.off(SOCKET_EVENTS.CHANNEL_KICKED, handleUnread);
      s.off(SOCKET_EVENTS.CHANNEL_MEMBER_KICKED, handleUnread);
      s.off(SOCKET_EVENTS.CHANNEL_MENTIONED, handleMentioned);
      s.off(SOCKET_EVENTS.CHANNEL_ARCHIVED, handleArchived);
      s.off(SOCKET_EVENTS.CHANNEL_UNARCHIVED, handleUnarchived);
    };

    if (socket) {
      registerListeners(socket);
    }

    // Poll for socket availability if not connected yet
    const interval = setInterval(() => {
      const current = getSocket();
      if (current && current !== socket) {
        if (socket) unregisterListeners(socket);
        socket = current;
        registerListeners(socket);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      if (socket) unregisterListeners(socket);
    };
  }, [queryClient]);

  const toggle = (section: string) => setCollapsed((p) => ({ ...p, [section]: !p[section] }));

  return (
    <div className="w-[280px] min-w-[280px] bg-white border-r border-gray-200 h-full flex flex-col overflow-hidden">
      {/* Header — 우측 채팅 헤더 h-14와 정렬, 검색창은 타이틀 우측에 인라인 배치 */}
      <div className="h-14 px-4 border-b border-gray-100 flex items-center gap-3 shrink-0">
        <h2 className="text-lg font-semibold text-gray-900 shrink-0">채팅</h2>
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="채널 검색..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 bg-gray-50"
          />
        </div>
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto py-2">
        {isLoading ? (
          <div className="space-y-2 px-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-9 bg-gray-100 rounded-md animate-pulse" />
            ))}
          </div>
        ) : channels.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8 px-4">
            참여 중인 채팅 채널이 없습니다.
          </p>
        ) : (
          <>
            {sections.favorites.length > 0 && (
              <Section title="즐겨찾기" collapsed={!!collapsed.favorites} onToggle={() => toggle('favorites')} icon={<Star className="size-3 text-yellow-500 fill-yellow-400" />}>
                {sections.favorites.map((ch) => (
                  <FavoriteChannelRow key={ch.id} channel={ch} active={ch.id === activeChannelId}
                    hasMention={mentionedChannels.has(ch.id)} hasDraft={!!getDraft(ch.id)}
                    onClick={() => { setMentionedChannels((p) => { const n = new Set(p); n.delete(ch.id); return n; }); navigate(`/chat/${ch.id}`); }} />
                ))}
              </Section>
            )}

            {sections.project.length > 0 && (
              <Section title="프로젝트" collapsed={!!collapsed.project} onToggle={() => toggle('project')}>
                {sections.project.map((ch) => (
                  <ProjectChannelRow key={ch.id} channel={ch} active={ch.id === activeChannelId}
                    hasMention={mentionedChannels.has(ch.id)} hasDraft={!!getDraft(ch.id)}
                    onClick={() => { setMentionedChannels((p) => { const n = new Set(p); n.delete(ch.id); return n; }); navigate(`/chat/${ch.id}`); }} />
                ))}
              </Section>
            )}

            {sections.user.length > 0 && (
              <Section title="채널" collapsed={!!collapsed.user} onToggle={() => toggle('user')}>
                {sections.user.map((ch) => (
                  <UserChannelRow key={ch.id} channel={ch} active={ch.id === activeChannelId}
                    hasMention={mentionedChannels.has(ch.id)} hasDraft={!!getDraft(ch.id)}
                    onClick={() => { setMentionedChannels((p) => { const n = new Set(p); n.delete(ch.id); return n; }); navigate(`/chat/${ch.id}`); }} />
                ))}
              </Section>
            )}

            {sections.dm.length > 0 && (
              <Section title="다이렉트 메시지" collapsed={!!collapsed.dm} onToggle={() => toggle('dm')}>
                {sections.dm.map((ch) => (
                  <DMChannelRow key={ch.id} channel={ch} active={ch.id === activeChannelId}
                    hasMention={mentionedChannels.has(ch.id)} hasDraft={!!getDraft(ch.id)}
                    onClick={() => { setMentionedChannels((p) => { const n = new Set(p); n.delete(ch.id); return n; }); navigate(`/chat/${ch.id}`); }} />
                ))}
              </Section>
            )}

            {/* Archive section */}
            {archivedChannels.length > 0 && (
              <Section title={`아카이브 (${archivedChannels.length})`} collapsed={!collapsed.archive} onToggle={() => toggle('archive')}>
                {collapsed.archive && archivedChannels.map((ch) => (
                  <button key={ch.id} onClick={() => navigate(`/chat/${ch.id}`)}
                    className={`flex items-center gap-2 w-full px-3 py-1 mx-2 rounded-md text-left transition-colors text-sm opacity-60 text-gray-500 hover:bg-gray-50
                      ${ch.id === activeChannelId ? 'bg-gray-100' : ''}`}
                    style={{ width: 'calc(100% - 16px)' }}>
                    <Archive className="size-3.5 shrink-0" />
                    <span className="truncate">
                      {ch.type === 'PROJECT' && ch.projectKey ? `[${ch.projectKey}] ` : ch.type === 'USER' ? '# ' : ''}
                      {ch.name}
                    </span>
                  </button>
                ))}
              </Section>
            )}

            {/* Message search results */}
            {debouncedSearch.length >= 2 && messageResults.length > 0 && (
              <Section title="메시지" collapsed={false} onToggle={() => {}}>
                {messageResults.map((r) => (
                  <button key={r.messageId}
                    onClick={() => navigate(`/chat/${r.channelId}?messageId=${r.messageId}`)}
                    className="w-full text-left px-3 py-1 mx-2 rounded-md hover:bg-gray-50 transition-colors"
                    style={{ width: 'calc(100% - 16px)' }}>
                    <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-0.5">
                      <span className="font-medium text-gray-700">{r.sender.name}</span>
                      <span>in</span>
                      <span className="truncate">{r.channelName}</span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-1"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.highlightedContent) }} />
                  </button>
                ))}
              </Section>
            )}
          </>
        )}
      </div>

      {/* Bottom action buttons */}
      <div className="border-t border-gray-100 px-3 h-14 flex items-center shrink-0">
        <div className="grid grid-cols-2 gap-1 w-full">
          <button onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
            <Plus className="size-3.5" /> 새 채널
          </button>
          <button onClick={() => setShowDMModal(true)}
            className="flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
            <Plus className="size-3.5" /> 새 DM
          </button>
        </div>
      </div>

      {showCreateModal && <CreateChannelModal onClose={() => setShowCreateModal(false)} />}
      {showDMModal && <CreateDMModal onClose={() => setShowDMModal(false)} />}
    </div>
  );
}

function Section({ title, collapsed, onToggle, children, icon }: {
  title: string; collapsed: boolean; onToggle: () => void; children: React.ReactNode; icon?: React.ReactNode;
}) {
  return (
    <div className="mb-1">
      <button onClick={onToggle}
        className="flex items-center gap-1 w-full px-3 py-1 text-sm font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
        {collapsed ? <ChevronRight className="size-3" /> : <ChevronDown className="size-3" />}
        {icon}
        {title}
      </button>
      {!collapsed && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="bg-blue-500 text-white text-sm rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-medium">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function MentionBadge() {
  return <span className="bg-red-500 text-white text-[11px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold">@</span>;
}

function DraftBadge() {
  return <PenLine className="size-3 text-orange-400 shrink-0" />;
}

function FavoriteChannelRow({ channel, active, onClick, hasMention, hasDraft }: { channel: ChatChannelItem; active: boolean; onClick: () => void; hasMention?: boolean; hasDraft?: boolean }) {
  const hasUnread = channel.unreadCount > 0;
  const userId = useAuthStore((s) => s.user?.id) ?? '';
  const participants = channel.dmParticipants ?? [];

  let icon: React.ReactNode;
  let label: string;
  if (channel.type === 'PROJECT') {
    icon = <Hash className="size-3.5 text-gray-400 shrink-0" />;
    label = channel.name ?? '';
  } else if (channel.type === 'DM') {
    icon = <MessageCircle className="size-3.5 text-gray-400 shrink-0" />;
    label = getDMDisplayName(participants, userId);
  } else {
    icon = <Hash className="size-3.5 text-gray-400 shrink-0" />;
    label = channel.name ?? '';
  }

  return (
    <button onClick={onClick}
      className={`flex items-center justify-between w-full px-3 py-1 mx-2 rounded-md text-left transition-colors text-sm
        ${active ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}
        ${hasUnread && !active ? 'font-semibold' : ''}`}
      style={{ width: 'calc(100% - 16px)' }}>
      <div className="flex items-center gap-1.5 min-w-0">
        {icon}
        <span className="truncate text-gray-800">{label}</span>
      </div>
      <div className="flex items-center gap-1">
        {hasDraft && <DraftBadge />}
        {hasMention && <MentionBadge />}
        <UnreadBadge count={channel.unreadCount} />
      </div>
    </button>
  );
}

function ProjectChannelRow({ channel, active, onClick, hasMention, hasDraft }: { channel: ChatChannelItem; active: boolean; onClick: () => void; hasMention?: boolean; hasDraft?: boolean }) {
  const hasUnread = channel.unreadCount > 0;
  return (
    <button onClick={onClick}
      className={`flex items-center justify-between w-full px-3 py-1 mx-2 rounded-md text-left transition-colors text-sm
        ${active ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}
        ${hasUnread && !active ? 'font-semibold' : ''}`}
      style={{ width: 'calc(100% - 16px)' }}>
      <div className="flex items-center gap-1.5 min-w-0">
        <Hash className="size-3.5 text-gray-400 shrink-0" />
        <span className="truncate text-gray-800">{channel.name}</span>
      </div>
      <div className="flex items-center gap-1">
        {hasDraft && <DraftBadge />}
        {hasMention && <MentionBadge />}
        <UnreadBadge count={channel.unreadCount} />
      </div>
    </button>
  );
}

function UserChannelRow({ channel, active, onClick, hasMention, hasDraft }: { channel: ChatChannelItem; active: boolean; onClick: () => void; hasMention?: boolean; hasDraft?: boolean }) {
  const hasUnread = channel.unreadCount > 0;
  return (
    <button onClick={onClick}
      className={`flex items-center justify-between w-full px-3 py-1 mx-2 rounded-md text-left transition-colors text-sm
        ${active ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}
        ${hasUnread && !active ? 'font-semibold' : ''}`}
      style={{ width: 'calc(100% - 16px)' }}>
      <div className="flex items-center gap-1.5 min-w-0">
        <Hash className="size-3.5 text-gray-400 shrink-0" />
        <span className="truncate text-gray-800">{channel.name}</span>
      </div>
      <div className="flex items-center gap-1">
        {hasDraft && <DraftBadge />}
        {hasMention && <MentionBadge />}
        <UnreadBadge count={channel.unreadCount} />
      </div>
    </button>
  );
}

function DMChannelRow({ channel, active, onClick, hasMention, hasDraft }: { channel: ChatChannelItem; active: boolean; onClick: () => void; hasMention?: boolean; hasDraft?: boolean }) {
  const hasUnread = channel.unreadCount > 0;
  const userId = useAuthStore((s) => s.user?.id) ?? '';
  const participants = channel.dmParticipants ?? [];
  const displayName = getDMDisplayName(participants, userId);

  return (
    <button onClick={onClick}
      className={`flex items-center justify-between w-full px-3 py-1 mx-2 rounded-md text-left transition-colors text-sm
        ${active ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}
        ${hasUnread && !active ? 'font-semibold' : ''}`}
      style={{ width: 'calc(100% - 16px)' }}>
      <div className="flex items-center gap-1.5 min-w-0">
        <MessageCircle className="size-3.5 text-gray-400 shrink-0" />
        <span className="truncate text-gray-800">{displayName}</span>
      </div>
      <div className="flex items-center gap-1">
        {hasDraft && <DraftBadge />}
        {hasMention && <MentionBadge />}
        <UnreadBadge count={channel.unreadCount} />
      </div>
    </button>
  );
}
